import AnsiUp from "ansi_up";
import { useToast } from "vue-toastification";
import { defineStore } from "pinia";
import { useApi, handleApiError } from "@/composables";
import { useBackendStore } from "@/stores";

// number 0-3 => string
const WEBSOCKET_STATUS = ["CONNECTING", "OPEN", "CLOSING", "CLOSED"] as const;
type WebsocketStatusTuple = typeof WEBSOCKET_STATUS;
export type WebsocketStatus = WebsocketStatusTuple[number];

// BuildJob status names
type BuildStatus = "PENDING" | "RUNNING" | "FAILURE" | "SUCCESS" | "CACHED";
export const BUILD_STATUS: Record<string, BuildStatus> = {
  P: "PENDING",
  R: "RUNNING",
  F: "FAILURE",
  S: "SUCCESS",
  C: "CACHED",
} as const;

const panic = (msg: string): never => {
  const toast = useToast();
  toast.error("Internal error.");
  throw Error(msg);
};

const ansiError = (text: string) => `\x1b[1;38;5;9m${text}\x1b[0m`;
const ansiUp = new AnsiUp();

const prepareUrl = (url_string: string): string => {
  try {
    const url = new URL(url_string);

    // HACK: DRF returns an url with incorrect port due to nginx reverse proxy
    if (url.host == "localhost" && url.port == "") {
      url.port = "8000";
    }

    // NOTE: DRF returns http urls due to nginx proxy, these are redirected to https
    // by nginx but e.g. Chrome will block such downloads due to "Mixed Content".
    if (url.protocol == "http:" && document.location.protocol == "https:") {
      url.protocol = "https:";
    }

    return url.toString();
  } catch {
    return url_string;
  }
};

export const useFirmwareStore = defineStore("Firmware", {
  state: () => {
    const api = useApi();
    return {
      api, // it seems that we can get cookies only in this constructor
      webSocket: null as WebSocket | null,
      webSocketReadyState: null as number | null,
      jobId: null as number | null,
      downloadUrl: null as string | null,
      buildJobStatus: null as string | null,
      consoleOutputRaw: "",
      consoleOutputHtml: "",
      consoleOutputCount: 0,
    };
  },

  getters: {
    // TODO: check if this would react to changes if we used webSocket.readyState indirectly
    webSocketStatus(state): WebsocketStatus | undefined {
      if (state.webSocketReadyState == null) return;
      return WEBSOCKET_STATUS[state.webSocketReadyState];
    },

    buildStatus(state): BuildStatus | undefined {
      if (state.buildJobStatus == null) return;
      return BUILD_STATUS[state.buildJobStatus];
    },

    consoleActive(): boolean {
      return this.webSocketStatus == "OPEN";
    },
  },

  actions: {
    consoleClear() {
      this.consoleOutputRaw = "";
      this.consoleOutputHtml = "";
      this.consoleOutputCount = 0;
    },

    consoleAppend(s: string) {
      this.consoleOutputRaw += s;
      this.consoleOutputHtml += ansiUp.ansi_to_html(s);
      this.consoleOutputCount += 1;
    },

    webSocketClose() {
      this.webSocket?.close();
      this.webSocketReadyState = null;
    },

    webSocketUpdateState() {
      if (this.webSocket == null) return;
      this.webSocketReadyState = this.webSocket.readyState;
    },

    onWebSocketMessage(e: MessageEvent) {
      const toast = useToast();

      const data = JSON.parse(e.data);

      if (data.output) {
        this.consoleAppend(data.output);
      }

      if (data.status) {
        const prevStatus = this.buildStatus;
        this.buildJobStatus = data.status;
        const status = this.buildStatus;

        if (
          prevStatus != data.status &&
          (status == "SUCCESS" || status == "CACHED")
        ) {
          this.fetchBuildResult();
        } else if (status == "FAILURE") {
          this.consoleAppend(ansiError("\nCOMPILATION FAILED!\n"));
          toast.error("Compilation failed.");
        }
      }

      if (!data.output && !data.status) {
        console.error(`Unexpected websocket message: ${data}`);
      }
    },

    async fetchBuildResult() {
      const toast = useToast();

      if (this.jobId == null) return;

      const response = await this.api.jobRetrieve(this.jobId);
      const jobStatus = response.data.status;
      const jobResults = response.data.results;

      if (jobStatus == undefined) {
        return panic("Missing field: BuildJob.stauts");
      } else if (jobResults == undefined) {
        return panic("Missing field: BuildJob.results");
      }

      const results = await this.api.resultsRetrieve(jobResults);
      const firmware = results.data.firmware;

      if (!firmware) {
        panic("Missing field: BuildResults.firmware");
        return; // panic's `never` type was not enough for typescript
      }

      this.downloadUrl = prepareUrl(firmware);

      if (BUILD_STATUS[jobStatus] == "CACHED") {
        toast.success("Existing firmware found, ready to download");
      } else {
        toast.success("Firmware is ready to download");
      }
    },

    invalidate() {
      // Reset state
      this.downloadUrl = null;
      this.consoleClear();
      this.webSocketClose();
    },

    async compileConfig(configId: number) {
      this.invalidate();

      // Queue a build job
      const response = await this.api.jobCreate({ config: configId });
      const jobId = response.data.id;
      const jobStatus = response.data.status;
      if (jobId == undefined) {
        return panic("Missing field: BuildJob.id");
      } else if (jobStatus == undefined) {
        return panic("Missing field: BuildJob.status");
      }

      // Finish if build results are cached
      this.jobId = jobId;
      this.buildJobStatus = jobStatus;
      if (this.buildStatus == "CACHED") {
        return await this.fetchBuildResult();
      }

      this.webSocket = this.api.openBuildJobWebSocket(this.jobId, {
        open: this.webSocketUpdateState,
        close: this.webSocketUpdateState,
        error: this.webSocketUpdateState,
        message: this.onWebSocketMessage,
      });
    },

    async compile() {
      const toast = useToast();
      const backend = useBackendStore();

      if (backend.currentConfig == null || backend.configModified) {
        toast.error("Current configuration has to be saved to server.");
        return;
      }

      return await this.compileConfig(backend.currentConfig.id).catch(
        handleApiError
      );
    },
  },
});
